import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import Header from '../../components/ui/Header';
import { Loader2, ArrowLeft, Building2, MessageCircle, MessageSquare, DollarSign, Calendar, Plane, Coffee, Luggage, ShieldCheck } from "lucide-react";

export default function CustomerMessagesV2OffersList() {
  const navigate = useNavigate();
  const { enquiryId } = useParams();
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id && enquiryId) {
      fetchEnquiryAndOffers();
    }
  }, [user, enquiryId]);

  const fetchEnquiryAndOffers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch enquiry details
      const { data: enquiryData, error: enquiryError } = await supabase?.from("enquiries")?.select("*")?.eq("id", enquiryId)?.eq("customer_id", user?.id)?.single();

      if (enquiryError) throw enquiryError;
      setEnquiry(enquiryData);

      // Fetch conversations (offers) with agent profiles - explicit join
      const { data: conversationsData, error: conversationsError } = await supabase?.from("conversations")?.select(`
          *,
          agent_profile:user_profiles!conversations_agent_id_fkey(
            id,
            full_name,
            company_name,
            role
          )
        `)?.eq("enquiry_id", enquiryId)?.order("created_at", { ascending: false });

      if (conversationsError) throw conversationsError;

      // Debug: Log the raw data to verify structure
      console.log('Fetched conversations with agent data:', conversationsData);

      setOffers(conversationsData || []);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOfferClick = (conversationId) => {
    navigate(`/customer/messages-v2/conversation/${conversationId}`);
  };

  const handleBack = () => {
    navigate("/customer/messages-v2");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Enquiries</span>
        </button>

        {/* Enquiry Header */}
        {enquiry && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {enquiry?.destination}
            </h1>
            <p className="text-gray-600 mb-4">{enquiry?.trip_type}</p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              {enquiry?.travel_dates && (
                <span>Dates: {enquiry?.travel_dates}</span>
              )}
              {enquiry?.budget && (
                <span className="font-medium">Budget: £{enquiry?.budget}</span>
              )}
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Offers Header */}
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Offers ({offers?.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Compare quotes from travel agents
          </p>
        </div>

        {/* Empty State */}
        {offers?.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Offers Yet
            </h3>
            <p className="text-gray-600">
              Agents will send you offers soon. Check back later!
            </p>
          </div>
        )}

        {/* Offers List */}
        <div className="divide-y divide-gray-100">
          {offers?.map((offer) => (
            <div
              key={offer?.id}
              onClick={() => handleOfferClick(offer?.id)}
              className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <h3 className="font-semibold text-gray-900">
                      {offer?.metadata?.agentCompanyName || offer?.agent_profile?.company_name || 'Travel Agency'}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    {offer?.metadata?.agentFullName || offer?.agent_profile?.full_name || 'Travel Agent'}
                  </p>
                </div>
                {offer?.quote_price && (
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-lg font-bold text-blue-600">
                      <DollarSign className="w-5 h-5" />
                      {offer?.quote_currency} {offer?.quote_price?.toLocaleString()}
                    </div>
                    {offer?.quote_submitted_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        Quote sent {new Date(offer.quote_submitted_at)?.toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Quote Details Summary */}
              {offer?.metadata && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  {offer?.metadata?.hotelName && (
                    <div className="flex items-start gap-2 text-sm">
                      <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-600">{offer?.metadata?.hotelName}</span>
                        {offer?.metadata?.boardBasis && (
                          <span className="text-gray-500 ml-2">
                            • {offer?.metadata?.boardBasis?.replace('_', ' ')?.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {offer?.metadata?.dates && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      {offer?.metadata?.dates?.flexible ? (
                        <span>Flexible dates</span>
                      ) : offer?.metadata?.dates?.startDate ? (
                        <span>
                          {new Date(offer.metadata.dates.startDate)?.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short'
                          })} - {new Date(offer.metadata.dates.endDate)?.toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      ) : null}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 text-xs">
                    {offer?.metadata?.flights?.included && (
                      <div className="flex items-center gap-1 text-gray-600 bg-blue-50 px-2 py-1 rounded">
                        <Plane className="w-3 h-3" />
                        <span>Flights included</span>
                      </div>
                    )}
                    {offer?.metadata?.transferIncluded && (
                      <div className="flex items-center gap-1 text-gray-600 bg-green-50 px-2 py-1 rounded">
                        <ShieldCheck className="w-3 h-3" />
                        <span>Transfer</span>
                      </div>
                    )}
                    {offer?.metadata?.baggageIncluded && (
                      <div className="flex items-center gap-1 text-gray-600 bg-purple-50 px-2 py-1 rounded">
                        <Luggage className="w-3 h-3" />
                        <span>Baggage</span>
                      </div>
                    )}
                    {offer?.metadata?.additionalInclusions && (
                      <div className="flex items-center gap-1 text-gray-600 bg-orange-50 px-2 py-1 rounded">
                        <Coffee className="w-3 h-3" />
                        <span>Extras</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 mt-3 text-sm text-blue-600 font-medium">
                <MessageSquare className="w-4 h-4" />
                <span>View conversation</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}